import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { horses } from '../data/mock';
import { ArrowLeft, Award, Trophy } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Horses = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section
        className="relative h-80 flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1682353213492-8433d437855a)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center text-white px-4 pt-16">
          <h1 className="text-5xl md:text-6xl font-black mb-4">{t('myHorses').split(' ')[0]} <span className="text-pink-500">{t('myHorses').split(' ').slice(1).join(' ') || t('horses')}</span></h1>
          <p className="text-xl md:text-2xl font-light text-zinc-300">{t('theIncredibleAthletes')}</p>
        </div>
      </section>

      {/* Horses Grid */}
      <section className="py-16 bg-zinc-950">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {horses.map((horse) => (
              <Link
                key={horse.id}
                to={`/horses/${horse.id}`}
                data-testid={`horse-card-${horse.id}`}
                className="group relative overflow-hidden bg-zinc-900 hover:bg-zinc-800 transition-colors"
              >
                {/* Horse Name on Top */}
                <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/90 via-black/70 to-transparent p-4">
                  <h2 className="text-xl font-black text-white">{horse.name}</h2>
                  {horse.feiId && (
                    <p className="text-xs text-cyan-400 mt-1">FEI: {horse.feiId}</p>
                  )}
                </div>
                
                {/* Image */}
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={horse.image}
                    alt={horse.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                {/* Info */}
                <div className="p-4">
                  <p className="text-pink-400 text-sm mb-2">{horse.subtitle}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-zinc-400">
                    <span className="bg-zinc-800 px-2 py-1">{horse.breed}</span>
                    <span className="bg-zinc-800 px-2 py-1">{t('born')} {horse.born}</span>
                    {horse.gender && <span className="bg-zinc-800 px-2 py-1">{horse.gender}</span>}
                  </div>
                  <p className="text-zinc-500 text-sm mt-3 line-clamp-2">{horse.description}</p>
                  <div className="mt-4 flex items-center text-cyan-400 text-sm font-medium group-hover:text-cyan-300 transition-colors">
                    <span>{t('viewProfile')}</span>
                    <ArrowLeft className="w-4 h-4 ml-2 rotate-180 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const HorseDetail = () => {
  const { id } = useParams();
  const horse = horses.find((h) => h.id === parseInt(id));
  const { t } = useLanguage();

  if (!horse) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">{t('horseNotFound')}</h2>
          <Link to="/horses" className="text-cyan-400 hover:text-cyan-300">
            {t('backToHorses')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section
        className="relative h-[70vh] flex items-end"
        style={{
          backgroundImage: `url(${horse.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        <div className="relative z-10 container mx-auto px-4 md:px-8 pb-12">
          <Link
            to="/horses"
            className="inline-flex items-center space-x-2 mb-6 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('backToHorses')}</span>
          </Link>
          <h1 className="text-5xl md:text-7xl font-black mb-2">{horse.name}</h1>
          <p className="text-xl text-pink-400">{horse.subtitle}</p>
          {horse.feiId && (
            <p className="text-cyan-400 mt-2">FEI ID: {horse.feiId}</p>
          )}
        </div>
      </section>

      {/* Details */}
      <section className="py-16 bg-zinc-950">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-zinc-900 border-l-4 border-pink-500 p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{t('breed')}</p>
              <p className="text-lg font-bold">{horse.breed}</p>
            </div>
            <div className="bg-zinc-900 border-l-4 border-cyan-400 p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{t('born')}</p>
              <p className="text-lg font-bold">{horse.born}</p>
            </div>
            <div className="bg-zinc-900 border-l-4 border-pink-500 p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{t('sire')}</p>
              <p className="text-lg font-bold">{horse.sire}</p>
            </div>
            {horse.gender && (
              <div className="bg-zinc-900 border-l-4 border-cyan-400 p-4">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{t('gender')}</p>
                <p className="text-lg font-bold">{horse.gender}</p>
              </div>
            )}
          </div>

          {horse.dam && (
            <div className="grid grid-cols-2 gap-4 mb-12">
              <div className="bg-zinc-900 border-l-4 border-zinc-600 p-4">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{t('dam')}</p>
                <p className="text-lg font-bold">{horse.dam}</p>
              </div>
              {horse.damSire && (
                <div className="bg-zinc-900 border-l-4 border-zinc-600 p-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{t('damSire')}</p>
                  <p className="text-lg font-bold">{horse.damSire}</p>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div className="mb-12">
            <p className="text-zinc-300 text-lg leading-relaxed">{horse.description}</p>
          </div>

          {/* Stats if available */}
          {horse.stats && (
            <div className="grid grid-cols-2 gap-4 mb-12">
              <div className="bg-gradient-to-r from-pink-900/30 to-zinc-900 p-6 text-center">
                <p className="text-4xl font-black text-pink-400">{horse.stats.starts}</p>
                <p className="text-zinc-400 text-sm uppercase tracking-wider">{t('feiStarts')}</p>
              </div>
              <div className="bg-gradient-to-r from-cyan-900/30 to-zinc-900 p-6 text-center">
                <p className="text-4xl font-black text-cyan-400">{horse.stats.wins}</p>
                <p className="text-zinc-400 text-sm uppercase tracking-wider">{t('wins')}</p>
              </div>
            </div>
          )}

          {/* Achievements */}
          <div>
            <h3 className="text-2xl font-black mb-6 flex items-center gap-3">
              <Trophy className="w-6 h-6 text-pink-500" />
              <span>{t('achievements')}</span>
            </h3>
            <ul className="space-y-3">
              {horse.achievements.map((achievement, index) => (
                <li key={index} className="flex items-start space-x-3 bg-zinc-900 p-4 border-l-2 border-cyan-400">
                  <Award className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span className="text-zinc-300">{achievement}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Horses;
